import { HoverCard, HoverCardTrigger } from '@radix-ui/react-hover-card';
import React, { FC, useMemo } from 'react';
import { PoolSizeAdjustmentReason } from 'shared';
import { twMerge } from 'tailwind-merge';

import { InfoCircle } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { HoverCardContent } from '../../../../components/hover-card/index';
import { Tooltip } from '../../../../components/tooltip/index';
import { useFormat } from '../../../../hooks/index';
import { Pool } from '../../../../types/pool-plans.types';
import { adjustPoolSizeStore } from '../../adjust-pool-size/index';
import { PoolPlansPopover } from '../pool-plans-popover';

type StickyPoolPlanItemProps = {
  companyId: string;
  date: Date;
  isSelected: boolean;
  pool: Pool;
  stakeholderId: string;
};

// Pool rows are leaf rows in By Stakeholder — no expand arrow and no nested plan rows.
export const StickyPoolPlanItem: FC<StickyPoolPlanItemProps> = ({ pool, isSelected }) => {
  const { format } = useFormat();

  return (
    <tr
      className={twMerge(
        'flex h-[72px] w-full border-l-[2px] border-l-brand-700 bg-white transition-colors',
        isSelected && 'bg-brand-25',
      )}
    >
      <td className="relative h-[72px] w-[49px] border-b-[1px] border-b-gray-100 bg-gray-50">
        <div className="flex h-full w-full items-center justify-center">
          <div className="w-[49px]" />
        </div>
      </td>
      <td className="flex h-[72px] w-full items-center gap-4 overflow-hidden text-nowrap border-b-[1px] border-b-gray-100 pb-4 pl-10 pr-4 pt-4">
        <div className="flex max-w-[300px] flex-col">
          <Tooltip content={pool?.name}>
            <span className="truncate text-sm font-[450] text-gray-900">{pool?.name}</span>
          </Tooltip>
        </div>
        {pool.isTerminated && (
          <HoverCard>
            <HoverCardTrigger>
              <InfoCircle className="size-4 cursor-pointer" />
            </HoverCardTrigger>
            <HoverCardContent
              className="z-20 w-max rounded-lg bg-[#101828] px-3 py-2"
              isShownTriangle
              side="top"
              triangleClassName="bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 ml-0"
            >
              <span className="text-label-md font-[550] text-white">
                <AppFormattedMessage id="TERMINATION_SET" />{' '}
                {pool?.terminationDate && format(pool?.terminationDate, 'dd/MM/yyyy')}
              </span>
            </HoverCardContent>
          </HoverCard>
        )}
      </td>
    </tr>
  );
};

type ScrollablePoolPlanItemProps = {
  companyId: string;
  date: Date;
  isSelected: boolean;
  onDeleteClick: (id: string) => void;
  onDeletePlanClick: (id: string) => void;
  onEditClick: (id: string) => void;
  onEditPlanClick: (id: string) => void;
  onEditTerminationDateClick: (id: string) => void;
  onViewClick: (id: string) => void;
  onViewPlanClick: (id: string) => void;
  pool: Pool;
  stakeholderId: string;
};

export const ScrollablePoolPlanItem: FC<ScrollablePoolPlanItemProps> = ({
  pool,
  isSelected,
  onEditClick,
  onViewClick,
  onDeleteClick,
  onEditTerminationDateClick,
  date,
}) => {
  const granted = pool?.granted?.toLocaleString('en-US');
  const totalGrantVested = pool?.totalGrantVested?.toLocaleString('en-US');
  const sharesVested = pool?.sharesVested?.toLocaleString('en-US');
  const exercisable = pool?.exercisable?.toLocaleString('en-US');
  const exercised = pool?.exercised?.toLocaleString('en-US');
  const expired = pool?.expired?.toLocaleString('en-US');
  const returnedToPool = pool.returnedToPool?.toLocaleString('en-US');
  const cashSettled = pool.cashSettled?.toLocaleString('en-US');
  const openAdjustPoolSize = adjustPoolSizeStore(({ open }) => open);

  const isTerminatedDatePassed = useMemo(() => {
    return pool?.terminationDate
      ? new Date(pool.terminationDate).getTime() < new Date().getTime()
      : false;
  }, [pool?.terminationDate]);

  return (
    <tr
      className={twMerge(
        'h-[72px] w-full border-b-[1px] border-b-gray-100 bg-white transition-colors',
        isSelected && 'bg-brand-25',
      )}
    >
      <td className="overflow-hidden p-4">
        <span
          className={twMerge(
            'max-w-[100px] truncate text-sm text-gray-700',
            !granted && 'text-gray-300',
          )}
        >
          {granted || '-'}
        </span>
      </td>
      <td className="overflow-hidden p-4">
        <span
          className={twMerge(
            'max-w-[100px] truncate text-sm text-gray-700',
            !totalGrantVested && 'text-gray-300',
          )}
        >
          {totalGrantVested || '-'}
        </span>
      </td>
      <td className="overflow-hidden p-4">
        <span
          className={twMerge(
            'max-w-[100px] truncate text-sm text-gray-700',
            !sharesVested && 'text-gray-300',
          )}
        >
          {sharesVested || '-'}
        </span>
      </td>
      <td className="overflow-hidden p-4">
        <span
          className={twMerge(
            'max-w-[100px] truncate text-sm text-gray-700',
            !exercisable && 'text-gray-300',
          )}
        >
          {exercisable || '-'}
        </span>
      </td>
      <td className="overflow-hidden p-4">
        <span
          className={twMerge(
            'max-w-[100px] truncate text-sm text-gray-700',
            !exercised && 'text-gray-300',
          )}
        >
          {exercised || '-'}
        </span>
      </td>
      <td className="overflow-hidden p-4">
        <span
          className={twMerge(
            'max-w-[100px] truncate text-sm text-gray-700',
            !cashSettled && 'text-gray-300',
          )}
        >
          {cashSettled || '-'}
        </span>
      </td>
      <td className="overflow-hidden p-4">
        <span
          className={twMerge(
            'max-w-[100px] truncate text-sm text-gray-700',
            !returnedToPool && 'text-gray-300',
          )}
        >
          {returnedToPool || '-'}
        </span>
      </td>
      <td className="overflow-hidden p-4">
        <span
          className={twMerge(
            'max-w-[100px] truncate text-sm text-gray-700',
            !expired && 'text-gray-300',
          )}
        >
          {expired || '-'}
        </span>
      </td>
      <td className="relative">
        <PoolPlansPopover
          date={date}
          id={pool.id}
          isTerminated={isTerminatedDatePassed}
          onAdjustSizeClick={(poolId) =>
            openAdjustPoolSize({ poolId, reason: PoolSizeAdjustmentReason.FORMAL_ADJUSTMENT })
          }
          onDeleteClick={onDeleteClick}
          onEditClick={onEditClick}
          onEditTerminationDateClick={onEditTerminationDateClick}
          onViewClick={onViewClick}
          text="Pool"
        />
      </td>
    </tr>
  );
};
